import {
  Dialog,
  DialogBackdrop,
  DialogDescription,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ExclamationIcon, XIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { isValidElement } from "react";
import { Button } from "../Button";

const sanitizeDescriptionNode = (node) => {
  if (node == null) return null;
  // Safe primitives — React escapes these automatically in JSX
  if (typeof node === "string" || typeof node === "number") return node;
  // Valid React elements (JSX) are safe; they never use dangerouslySetInnerHTML here
  if (isValidElement(node)) return node;
  // Reject anything else (arrays of raw HTML, objects, etc.)
  return null;
};

export const ModalAlert = ({
  open,
  setOpen,
  title,
  description,
  descriptionNode = null,
  confirmButtonText,
  cancelButtonText,
  onConfirm,
  onCancel = () => setOpen(false),
  loadingButton,
  disabledButton,
  type = "danger",
}) => {
  const { t } = useTranslation();
  const safeDescriptionNode = sanitizeDescriptionNode(descriptionNode);

  const getThemeColor = () => {
    switch (type) {
      case "success":
        return "green";
      case "warning":
        return "yellow";
      case "danger":
        return "red";
      case "primary":
        return "blue";
      default:
        return "gray";
    }
  };

  const getExclamationIconColor = () => {
    switch (type) {
      case "success":
        return "text-green-600";
      case "warning":
        return "text-yellow-600";
      case "danger":
        return "text-red-600";
      case "primary":
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <Transition show={open}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={setOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop
              transition
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            />
          </TransitionChild>
          {/* Headless UI marks the backdrop aria-hidden, so the panel cannot
              live inside it. This box is the one the backdrop used to give it. */}
          <div className="fixed inset-0">
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={onCancel}
                  >
                    <span className="sr-only">{t("common.close")}</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div
                    className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-${getThemeColor()}-50 sm:mx-0 sm:h-10 sm:w-10`}
                  >
                    <ExclamationIcon
                      className={`h-6 w-6 ${getExclamationIconColor()}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {t(title)}
                    </DialogTitle>
                    <div className="mt-2">
                      <DialogDescription
                        as="p"
                        className="text-sm text-gray-500"
                      >
                        {safeDescriptionNode != null
                          ? safeDescriptionNode
                          : t(description)}
                      </DialogDescription>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <Button
                    disabled={disabledButton}
                    loading={loadingButton}
                    title={t(confirmButtonText)}
                    color={getThemeColor()}
                    type="button"
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${getThemeColor()}-600 text-base font-medium text-white hover:bg-${getThemeColor()}-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${getThemeColor()}-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400`}
                    onClick={onConfirm}
                  />
                  {cancelButtonText && (
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={onCancel}
                      data-autofocus
                    >
                      {t(cancelButtonText)}
                    </button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
